import { useState } from 'react';
import { Box, Typography, TextField, IconButton, Collapse } from '@mui/material';
import StarIcon         from '@mui/icons-material/Star';
import StarBorderIcon   from '@mui/icons-material/StarBorder';
import EditIcon         from '@mui/icons-material/Edit';
import AddIcon          from '@mui/icons-material/Add';
import CloseIcon        from '@mui/icons-material/Close';
import { FEEDBACK_STAR_THRESHOLD } from '../../constants/director-constants';

const COMMENT_TYPE_OPTIONS = [
  { value: 'event', label: 'El evento', bg: '#FFF8F0', color: '#B45309' },
  { value: 'judge', label: 'El juez',   bg: '#F0F5FF', color: '#1D4ED8' },
  { value: 'both',  label: 'Ambos',     bg: '#F0FDF4', color: '#166534' },
];

function StarRating({ value, onChange, disabled }) {
  const [hovered, setHovered] = useState(0);
  return (
    <Box sx={{ display: 'flex', gap: 0.25 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <IconButton
          key={n}
          size="small"
          disabled={disabled}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          sx={{ p: 0.25, color: (hovered || value) >= n ? '#F59E0B' : '#D1D5DB' }}
        >
          {(hovered || value) >= n ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
      ))}
    </Box>
  );
}

/**
 * Props:
 *  stars, onStarsChange
 *  comment, onCommentChange
 *  commentType, onCommentTypeChange
 *  hasExistingNote  — true si ya hay una nota guardada en DB
 *  disabled         — bloquea todo (evento sin calificación o sin sync)
 */
function FeedbackWidget({ stars, onStarsChange, comment, onCommentChange, commentType, onCommentTypeChange, hasExistingNote, disabled }) {
  const isRequired = stars > 0 && stars <= FEEDBACK_STAR_THRESHOLD;
  const [expanded, setExpanded] = useState(isRequired || hasExistingNote);

  const handleStarsChange = (n) => {
    onStarsChange(n);
    const nowRequired = n <= FEEDBACK_STAR_THRESHOLD;
    if (nowRequired) setExpanded(true);
  };

  const handleToggleNote = () => {
    if (expanded && !isRequired) {
      setExpanded(false);
      onCommentChange('');
      onCommentTypeChange(null);
    } else {
      setExpanded(true);
    }
  };

  return (
    <Box>
      {/* ── Estrellas ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <StarRating value={stars} onChange={handleStarsChange} disabled={disabled} />

        {/* Botón nota (solo si no es requerido) */}
        {stars > 0 && !isRequired && !disabled && (
          <Box
            component="button"
            onClick={handleToggleNote}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: 'text.secondary',
              fontSize: '0.78rem',
              p: 0,
              '&:hover': { color: '#284B32' },
            }}
          >
            {expanded && !isRequired ? (
              <><CloseIcon sx={{ fontSize: 14 }} /> Quitar nota</>
            ) : hasExistingNote ? (
              <><EditIcon sx={{ fontSize: 14 }} /> Editar nota</>
            ) : (
              <><AddIcon sx={{ fontSize: 14 }} /> Agregar nota</>
            )}
          </Box>
        )}
      </Box>

      {/* ── Área de nota ── */}
      <Collapse in={expanded && stars > 0 && !disabled} unmountOnExit>
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Selector commentType */}
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
              Sobre{isRequired ? ' *' : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {COMMENT_TYPE_OPTIONS.map(({ value, label, bg, color }) => (
                <Box
                  key={value}
                  component="button"
                  onClick={() => onCommentTypeChange(value)}
                  sx={{
                    border: `1px solid ${commentType === value ? color : '#D1D5DB'}`,
                    borderRadius: 1,
                    px: 1.25,
                    py: 0.4,
                    fontSize: '0.78rem',
                    fontWeight: commentType === value ? 600 : 400,
                    background: commentType === value ? bg : '#fff',
                    color: commentType === value ? color : 'text.secondary',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Textarea */}
          <TextField
            multiline
            minRows={2}
            maxRows={5}
            size="small"
            placeholder="Escribe tu observación…"
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            required={isRequired}
            label={isRequired ? 'Comentario *' : 'Comentario'}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

export default FeedbackWidget;
